import React, {useMemo} from 'react';
import type {SxProps, Theme} from '@mui/material';
import {
  FormControl,
  ListSubheader,
  MenuItem,
  Select as MuiSelect,
} from '@mui/material';

type GroupItemName = 'ListSubheaderGroupItem';
export const GroupItemNameValue: GroupItemName = 'ListSubheaderGroupItem';

export type LabelValue<T> = {
  value: T;
  label: string;
};

const isLabelValue = <T,>(value: unknown): value is LabelValue<T> => {
  return !!(value as LabelValue<T>)?.value && !!(value as LabelValue<T>)?.label;
};

/**
 * Option can be a group, a string value or an object value
 */
type OptionValue<T> = LabelValue<T> | T | {value: GroupItemName; label: string};

export type SelectProps<T = string | number> = {
  value?: T;
  options: ReadonlyArray<OptionValue<T>>;
  onChange: (value: T) => void;
  error?: boolean;
  disabled?: boolean;
  required?: boolean;
};

const SelectSx: SxProps = {
  height: 42,
};

const GroupSx: SxProps<Theme> = (theme: Theme) => ({
  color: theme.palette.primary.main,
  textDecoration: 'underline',
  paddingLeft: theme.spacing(2.5),
});

export function Select<T extends string | number>({
  options,
  value,
  onChange,
  disabled,
  required,
  error,
}: React.PropsWithChildren<SelectProps<T>>) {
  const MenuItems = useMemo(() => {
    return options.map(o => {
      if (!isLabelValue(o)) {
        return (
          <MenuItem key={o.toString()} value={o}>
            {o.toString()}
          </MenuItem>
        );
      }

      // Renders group separator
      if (o.value === GroupItemNameValue) {
        return (
          <ListSubheader sx={GroupSx} key={`${o.value}-${o.label}`}>
            {o.label}
          </ListSubheader>
        );
      }

      return (
        <MenuItem key={o.value} value={o.value}>
          {o.label ?? o.value}
        </MenuItem>
      );
    });
  }, [options]);

  return (
    <FormControl fullWidth error={error}>
      <MuiSelect<T>
        required={required}
        value={value}
        disabled={disabled}
        onChange={event => onChange(event.target.value as T)}
        sx={SelectSx}>
        {MenuItems}
      </MuiSelect>
    </FormControl>
  );
}
